"use client";
import React, { useState, useEffect, useRef, ReactNode } from "react";
import { ChevronDown } from "lucide-react";

interface DropdownItem {
  title: string;
  onClick: () => void;
  image?: ReactNode;
}

interface DropdownButtonProps {
  title: string; // Compulsory
  onClick: () => void; // Main button click
  secondaryTitle?: string; // Optional small text beside main title
  image?: ReactNode;
  items?: DropdownItem[]; // Optional dropdown list
  disabled?: boolean;
}

export const DropdownButton: React.FC<DropdownButtonProps> = ({
  title,
  onClick,
  secondaryTitle,
  image,
  items = [],
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const hasList = items.length > 0;

  return (
    <div ref={dropdownRef} className="relative inline-block text-left m-2">
      <div
        className={`flex items-center rounded shadow-md transition duration-300 ease-in-out overflow-hidden 
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} 
          bg-blue-500 text-[var(--primaryText)]`}
      >
        {/* Left side (main button) */}
        <button
          onClick={onClick}
          disabled={disabled}
          className={`flex items-center gap-2 px-4 py-2 font-semibold flex-1 ${
            hasList ? "justify-start" : "justify-center"
          }`}
        >
          {image && (
            <span className="w-4 h-4 flex-shrink-0">{image}</span>
          )}
          <span className="text-sm font-[500] text-white">{title}</span>
          {secondaryTitle && (
            <span className="ml-1 text-xs opacity-80">{secondaryTitle}</span>
          )}
        </button>

        {/* Right side (chevron toggle) */}
        {hasList && (
          <button
            onClick={() => setOpen(!open)}
            className="px-2 py-2 flex items-center justify-center border-l-2 hover:bg-blue-600/70 transition"
            style={{ borderColor: "#c0c0c0" }}
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>
        )}
      </div>

      {/* Dropdown List */}
      {open && hasList && (
        <div className=" absolute left-full bottom-2 w-48 bg-background border border-primaryText rounded shadow-lg z-20 animate-fadeIn">
          <ul className="space-y-1">
            {items.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm rounded text-[var(--primaryText)] hover:text-[var(--primaryText)] hover:bg-[var(--secondaryBackground)] transition"
                >
                  {item.image && (
                    item.image
                  )}
                  <span>{item.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

interface ButtonProps {
  children?: React.ReactNode;
  text?: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  text,
  onClick,
  disabled = false,
  variant = 'primary',
  className = ''
}) => {
  const baseClasses = "px-4 py-2 font-semibold rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-opacity-75 transition duration-300 ease-in-out";

  const variantClasses = variant === 'primary'
    ? "bg-blue-500 text-[var(--primaryText)] hover:bg-blue-600 focus:ring-blue-400"
    : "bg-gray-200 text-[var(--primaryText)] hover:bg-gray-300 focus:ring-gray-400";

  const disabledClasses = disabled
    ? "opacity-50 cursor-not-allowed"
    : "cursor-pointer";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses} ${disabledClasses} ${className}`}
    >
      {children || text}
    </button>
  );
};